// Innehållsmodell för Fennys fördjupade fotosidor (Etapp 1 och 3).
// En post = en landningssida med eget sökjobb, egen FAQ och eget bildurval.
// Sanningskälla för generatorn i src/lib/seo/fotoTopicRoute.tsx.

import type { PhotoCategory } from "@/content/photo-gallery";
import { fotoRoleTopics } from "@/content/foto-roles";

export interface FotoTopicFAQ {
  q: string;
  a: string;
}

export interface FotoTopicSection {
  id: string;
  heading: string;
  paragraphs: string[];
}

export interface FotoTopic {
  slug: string;
  /** Kort etikett för navigation och internlänkar. */
  nav: string;
  /** Rubrik i brödsmulor. */
  crumb: string;
  title: string;
  metaTitle: string;
  description: string;
  eyebrow: string;
  h1: string;
  lede: string;
  /** Citerbart kortsvar — måste matcha schemats acceptedAnswer ordagrant. */
  quickAnswer: string;
  /** Frågan kortsvaret besvarar. */
  question: string;
  /** Id i src/lib/aeo/answers.ts som renderas som citerbart kortsvar. */
  answerId?: string;
  serviceType: string;
  gallery: PhotoCategory | "alla";
  galleryHeading: string;
  bullets: { title: string; body: string }[];
  sections: FotoTopicSection[];
  faq: FotoTopicFAQ[];
  knowsAbout: string[];
  /** Interna länkar som visas i "läs vidare". */
  related: { label: string; to: string }[];
  /** Prisrader när sidan är en prissida. */
  pricing?: {
    intro: string;
    rows: { format: string; scope: string; price: string; lead?: string }[];
    footnote?: string;
  };
  process?: { label: string; body: string; duration?: string }[];
  /** Utvalda filmcase som visas som tysta autoplay-rutor under galleriet. */
  featuredVideos?: {
    heading: string;
    intro?: string;
    items: { youtubeId: string; title: string }[];
  };
}

const LEV = "Retuscherade bilder levereras normalt inom 3–5 arbetsdagar.";
const OFFERT =
  "Fenny arbetar med offert per uppdrag i stället för fast prislista, och priset låses innan dagen bokas. Kontakt: Fenny, hey@fenny.se.";

const baseTopics: FotoTopic[] = [
  {
    slug: "headshots",
    answerId: "sv_vad_kostar_ett_headshot",
    nav: "Headshots & LinkedIn-porträtt",
    crumb: "Headshots",
    title: "Headshots",
    metaTitle: "Headshots och LinkedIn-porträtt för företag | Fenny Foto",
    description:
      "Professionella headshots för LinkedIn, sajt och pressrum. Mobil studio i era lokaler, 5–10 minuter per person, leverans inom 3–5 arbetsdagar.",
    eyebrow: "Headshots",
    h1: "Headshots som ser ut som personen — inte som ett passfoto.",
    lede:
      "Ett headshot är ofta det första en kund, kandidat eller investerare ser av er. Vi fotograferar hela organisationen på en dag, med samma ljus och samma beskärning, och dokumenterar uppsättningen så att nyanställda kan matchas in senare.",
    question: "Vad kostar och innehåller ett professionellt headshot för företag?",
    quickAnswer:
      "Ett professionellt headshot tar 5–10 minuter per person när fotograferingen sker på plats med mobil studio, och en dag räcker normalt till 20–40 medarbetare. Priset offereras per uppdrag utifrån antal personer, retuschnivå och användningsrätt — inte per bild. Fenny levererar retuscherade bilder inom 3–5 arbetsdagar i format för LinkedIn, sajt och pressrum, och dokumenterar ljusuppsättningen så att nyanställda kan fotograferas in i samma serie senare. Kontakt: Fenny, hey@fenny.se.",
    serviceType: "Headshots",
    gallery: "portratt",
    galleryHeading: "Headshots ur skarpa uppdrag",
    bullets: [
      { title: "5–10 minuter per person", body: "Schemalagt så att ingen står och väntar och ingen dag går förlorad." },
      { title: "Samma manér hela vägen", body: "Ljus, bakgrund och beskärning dokumenteras och kan återskapas exakt." },
      { title: "Färdiga format", body: "Kvadratiskt för LinkedIn, stående för sajten, friläggbart för pressrum." },
    ],
    sections: [
      {
        id: "vad-ar-ett-headshot",
        heading: "Vad skiljer ett headshot från ett vanligt porträtt?",
        paragraphs: [
          "Ett headshot är beskuret tätt kring ansikte och axlar och har ett enda jobb: att göra personen igenkännbar och trovärdig i ett litet format. Ett företagsporträtt kan vara helkropp, i miljö och berätta om roll och sammanhang.",
          "I praktiken beställer de flesta bolag båda: ett tight headshot för LinkedIn och intranät, och ett friare porträtt i miljö för sajt och presentationer. Vi fotograferar båda i samma pass utan att dagen blir längre.",
        ],
      },
      {
        id: "sa-gar-det-till",
        heading: "Så går en headshot-dag till",
        paragraphs: [
          "Vi bygger mobil studio i ett konferensrum — det behövs ungefär tre gånger tre meter, ett eluttag och 45 minuters riggning. Därefter kör vi enligt tidsschema.",
          "Varje person får se ett par bilder direkt i skärmen, vilket gör att även de som ogärna blir fotograferade slappnar av. Efter dagen väljer ni bilder i ett digitalt galleri och vi retuscherar det urvalet.",
        ],
      },
      {
        id: "retusch",
        heading: "Retuschnivå: varsam som standard",
        paragraphs: [
          "Standard är färgsättning, jämnat ljus och borttagning av tillfälligheter som en lös tråd eller en glansig panna. Ansiktsdrag ändras inte. Personen ska kunna klicka på bilden och känna igen sig.",
          "Tyngre retusch — frilägg, byte av bakgrund, kompositioner — offereras per bild när det behövs.",
        ],
      },
    ],
    faq: [
      {
        q: "Hur många hinner ni fotografera på en dag?",
        a: "Med tidsschema och mobil studio hinner vi normalt 20–40 personer på en dag. Ska alla dessutom ha ett miljöporträtt landar dagen närmare 20.",
      },
      {
        q: "Vad ska medarbetarna ha på sig?",
        a: "Vi skickar en kort klädguide i förväg: enfärgade plagg fungerar bäst, undvik smala mönster som flimrar och ta gärna med ett extra plagg. Ingen behöver klä upp sig mer än vad som är normalt på er arbetsplats.",
      },
      {
        q: "Kan nyanställda fotograferas in senare?",
        a: "Ja. Vi dokumenterar ljusuppsättning, avstånd, objektiv och beskärning. En kompletteringsfotografering ett halvår senare matchar därför originalserien.",
      },
      {
        q: "Får vi använda bilderna fritt?",
        a: "Fri användningsrätt i era egna kanaler ingår. Ska bilderna användas i köpt media eller print i stor upplaga specificeras det separat i offerten.",
      },
      {
        q: "Kan ni fotografera på distans eller på flera orter?",
        a: "Ja. Vi kör samma uppsättning på flera kontor så att bilderna hänger ihop oavsett var personen sitter. Resa specificeras i offerten.",
      },
    ],
    knowsAbout: ["Headshots", "LinkedIn-porträtt", "Företagsporträtt", "Mobil studio", "Fotograf Stockholm"],
    related: [
      { label: "Företagsporträtt", to: "/foto/foretagsportratt" },
      { label: "Studiofotografering", to: "/foto/studiofotografering" },
      { label: "Vad kostar en fotograf?", to: "/foto/vad-kostar-en-fotograf" },
    ],
  },
  {
    slug: "fotoagentur",
    answerId: "sv_fotoagentur_eller_frilans",
    nav: "Fotoagentur",
    crumb: "Fotoagentur",
    title: "Fotoagentur",
    metaTitle: "Fotoagentur för företag — en leverantör för bild | Fenny",
    description:
      "Fotoagentur som tar hela bildbehovet: porträtt, kampanj, produkt och event med samma manér. En kontaktperson, ett avtal, en bildbank. Offert per uppdrag.",
    eyebrow: "Fotoagentur",
    h1: "En fotoagentur i stället för fem frilansare.",
    lede:
      "När bilderna kommer från olika håll ser sajten ut som fem olika bolag. Vi tar hela bildbehovet under ett manér, med en kontaktperson, ett avtal och en bildbank som faktiskt används.",
    question: "Vad är skillnaden mellan att anlita en fotoagentur och en frilansfotograf?",
    quickAnswer:
      "En frilansfotograf löser ett enskilt uppdrag, medan en fotoagentur ansvarar för hela bildflödet över tid: manér, planering, bemanning, efterarbete, bildbank och rättigheter. Skillnaden märks framför allt när flera bildtyper ska hänga ihop eller när samma manér ska hålla i två år. Fenny arbetar som fotoagentur med en fast kontaktperson, dokumenterat bildmanér och offert per uppdrag. Kontakt: Fenny, hey@fenny.se.",
    serviceType: "Fotoagentur",
    gallery: "alla",
    galleryHeading: "Ett manér, flera bildtyper",
    bullets: [
      { title: "En kontaktperson", body: "Fenny äger uppdraget från brief till leverans — ingen stafett mellan konton." },
      { title: "Dokumenterat manér", body: "Ljus, färg och beskärning skrivs ned så att bilderna hänger ihop över år." },
      { title: "Foto och film i samma hus", body: "Fenny är i grunden ett filmproduktionsbolag — samma dag kan ge båda." },
    ],
    sections: [
      {
        id: "nar-behovs-en-agentur",
        heading: "När en agentur är rätt val",
        paragraphs: [
          "Behöver ni en bild till en artikel räcker en frilansare. Behöver ni porträtt av hela organisationen, kampanjbilder till fyra format, produktbilder till e-handeln och eventbilder tre gånger om året, är samordningen större än fotograferingen.",
          "Det är där en agentur tjänar in sig: samma manér, gemensam planering, en gemensam bildbank och en part som ansvarar för att det används.",
        ],
      },
      {
        id: "sa-arbetar-vi",
        heading: "Så arbetar vi som fotoagentur",
        paragraphs: [
          "Vi börjar med en bildinventering: vad finns, vad används, vad saknas och vad förväntas de närmaste tolv månaderna. Utifrån den lägger vi en fotoplan med två till fyra fototillfällen per år i stället för femton akuta beställningar.",
          "Varje tillfälle offereras för sig, men manéret, filnamnsstandarden och bildbanksstrukturen är gemensam. Det gör att bilderna hittas — den vanligaste anledningen till att ett bolag beställer om bilder de redan har.",
        ],
      },
      {
        id: "rattigheter",
        heading: "Rättigheter och avtal",
        paragraphs: [
          "Fri användningsrätt i era egna kanaler ingår alltid. Köpt media, utomhusreklam och tryck i stor upplaga specificeras separat, särskilt när modeller medverkar.",
          "Medverkandeavtal för medarbetare hanteras i samband med fotograferingen så att ni inte upptäcker glapp när bilden ska annonseras.",
        ],
      },
    ],
    faq: [
      {
        q: "Är en fotoagentur dyrare än en frilansare?",
        a: "Per fotograferingsdag är prisbilden likartad. Skillnaden ligger i att planering, manér, bildbank och rättigheter ingår, vilket normalt minskar antalet omfotograferingar.",
      },
      {
        q: "Måste vi teckna abonnemang?",
        a: "Nej. Varje uppdrag kan beställas enskilt. Många väljer ändå en fotoplan över året eftersom det ger bättre pris per tillfälle och en jämnare bildström.",
      },
      {
        q: "Kan ni ta över efter en tidigare fotograf?",
        a: "Ja. Vi analyserar befintligt bildmaterial och kan antingen matcha in oss i det befintliga manéret eller föreslå ett nytt och fasa över gradvis.",
      },
      {
        q: "Arbetar ni i hela Sverige?",
        a: "Ja. Vi utgår från Stockholm, Västerås och Malmö och producerar i hela landet, med ortssidor för de största städerna.",
      },
    ],
    knowsAbout: ["Fotoagentur", "Bildproduktion", "Bildmanér", "Bildbank", "Företagsfotografering"],
    related: [
      { label: "Företagsfotografering", to: "/foto/foretagsfotografering" },
      { label: "Bildbank", to: "/foto/bildbank" },
      { label: "Jämför upplägg", to: "/foto/jamfor" },
    ],
  },
  {
    slug: "dronarfoto",
    answerId: "sv_dronarfoto_regler",
    nav: "Drönarfoto",
    crumb: "Drönarfoto",
    title: "Drönarfoto",
    metaTitle: "Drönarfoto — flygbilder av fastighet och projekt | Fenny",
    description:
      "Drönarfoto med certifierad pilot: anläggningar, fastigheter, byggprojekt och event ur luften. Tillstånd och försäkring ingår. Offert per uppdrag.",
    eyebrow: "Drönarfoto",
    h1: "Flygbilder som förklarar hela anläggningen på en bild.",
    lede:
      "En markbild visar en fasad. En flygbild visar sammanhanget: hur stort området är, hur logistiken hänger ihop och hur projektet växer. Vi flyger med certifierad pilot, försäkring och de tillstånd platsen kräver.",
    question: "Vad gäller för drönarfoto i Sverige och vad kostar det?",
    quickAnswer:
      "Drönarfotografering i Sverige kräver att operatören är registrerad hos Transportstyrelsen och att piloten har rätt behörighet för den kategori flygningen sker i, samt särskilt tillstånd nära flygplatser, över folksamlingar och inom vissa skyddsområden. Vädret styr genomförandet: hård vind eller regn flyttar passet. Ett flygpass tar normalt en till tre timmar på plats och kan kombineras med markfotografering samma dag. Fenny offererar per uppdrag. Kontakt: Fenny, hey@fenny.se.",
    serviceType: "Drönarfotografering",
    gallery: "dronare",
    galleryHeading: "Flygbilder ur skarpa uppdrag",
    featuredVideos: {
      heading: "Utvalda case med flygbild",
      intro:
        "Samma pilot och samma utrustning ligger bakom rörligt material i de här produktionerna — industri, jubileum och bröllop.",
      items: [
        { youtubeId: "HwWGVOl6dtg", title: "Huawei Energy Solution in Denmark — ALSO / Huawei" },
        { youtubeId: "KkE8BqgQ14M", title: "30 år med Garageportexperten — Garageportexperten" },
        { youtubeId: "2efcFS-3yHU", title: "Bröllopsfilm — Mattias & Linnea" },
      ],
    },
    bullets: [
      { title: "Certifierad pilot", body: "Registrerad operatör, rätt behörighet och försäkring för uppdraget." },
      { title: "Tillstånd hanteras", body: "Vi kontrollerar luftrum och söker de tillstånd platsen kräver." },
      { title: "Foto och film i samma flygning", body: "Stillbild och rörligt från samma pass sänker kostnaden." },
    ],
    sections: [
      {
        id: "anvandningsomraden",
        heading: "Vad flygbilder används till",
        paragraphs: [
          "Vanligast är anläggningsöversikter, byggprojekt i etapper, fastighetsmaterial vid uthyrning och försäljning, samt event där platsen i sig är en del av berättelsen.",
          "Ett återkommande upplägg är kvartalsvisa flygningar över samma byggprojekt från exakt samma position — det ger en serie som visar utvecklingen och används flitigt i rapportering och kommunikation.",
        ],
      },
      {
        id: "planering",
        heading: "Planering och väder",
        paragraphs: [
          "Vi kontrollerar luftrum, närhet till flygplats, skyddsobjekt och eventuella restriktioner innan datum spikas. Är platsen restriktionsbelagd söker vi tillstånd i god tid.",
          "Väder är den vanligaste orsaken till ombokning. Vi lägger därför in ett reservdatum i planeringen utan extra kostnad.",
        ],
      },
    ],
    faq: [
      {
        q: "Får ni flyga över vår anläggning?",
        a: "I de flesta fall ja, med markägarens medgivande. Nära flygplatser, över skyddsobjekt eller folksamlingar krävs särskilt tillstånd, vilket vi kontrollerar och söker.",
      },
      {
        q: "Vad händer om vädret ställer in?",
        a: "Vi bokar om till reservdatumet. Ingen flygavgift debiteras för ett pass som inte kunnat genomföras på grund av väder.",
      },
      {
        q: "Kan ni fotografera personer ur luften?",
        a: "Vi undviker flygning direkt över folksamlingar. Vid event flyger vi i anslutning till området och planerar bildvinklarna därefter.",
      },
      {
        q: "Levererar ni även rörligt?",
        a: "Ja. Samma flygpass kan ge både stillbilder och filmsekvenser, vilket är den mest kostnadseffektiva ordningen.",
      },
    ],
    knowsAbout: ["Drönarfoto", "Flygfotografering", "Byggdokumentation", "Fastighetsfoto"],
    related: [
      { label: "Bildbank", to: "/foto/bildbank" },
      { label: "Eventfoto", to: "/foto/eventfoto" },
      { label: "Foto och film", to: "/foto/foto-och-film" },
    ],
  },
  {
    slug: "bildbank",
    answerId: "sv_bygga_bildbank",
    nav: "Bildbank",
    crumb: "Bildbank",
    title: "Bildbank",
    metaTitle: "Bildbank för företag — så bygger ni den | Fenny",
    description:
      "Så bygger och underhåller ett företag en bildbank som faktiskt används: struktur, namngivning, rättigheter, gallring och påfyllning två gånger per år.",
    eyebrow: "Bildbank",
    h1: "En bildbank är inte en mapp — det är ett arbetssätt.",
    lede:
      "De flesta bolag har fler bilder än de tror och hittar färre än de behöver. Vi bygger struktur, namngivning och rättighetsordning, och fyller på i planerad takt i stället för i panik före varje lansering.",
    question: "Hur bygger man en bildbank för ett företag?",
    quickAnswer:
      "En användbar bildbank byggs i fyra steg: inventera och gallra befintligt material, sätt en struktur som utgår från användning snarare än fotograferingstillfälle, namnge filerna enhetligt med datum, motiv och rättighetsstatus, och planera påfyllning två till fyra gånger per år. Varje bild bör bära information om fotograf, datum och nyttjanderätt, annars blir den oanvändbar i annonsering. Fenny sätter upp strukturen och fyller på enligt fotoplan. Kontakt: Fenny, hey@fenny.se.",
    serviceType: "Bildbank och bildhantering",
    gallery: "alla",
    galleryHeading: "Material ur kunduppdrag",
    bullets: [
      { title: "Struktur efter användning", body: "Mappar för sajt, social, sälj och press slår mappar per fotodatum." },
      { title: "Rättigheter per bild", body: "Modellavtal och nyttjandeperiod följer med filen, inte ett mejl från 2023." },
      { title: "Planerad påfyllning", body: "Två till fyra fototillfällen per år i stället för akuta beställningar." },
    ],
    sections: [
      {
        id: "inventering",
        heading: "Steg 1: inventera och gallra",
        paragraphs: [
          "Vi går igenom befintligt material och sorterar i tre högar: används, kan användas efter bearbetning, och bör pensioneras. Bilder med utgången nyttjanderätt eller personer som slutat plockas bort.",
          "Gallringen är den viktigaste delen. En bildbank med 4 000 filer där 300 är godkända används inte; en med 300 godkända gör det.",
        ],
      },
      {
        id: "struktur",
        heading: "Steg 2: struktur och namngivning",
        paragraphs: [
          "Strukturen ska följa hur bilderna används — sajt, sociala kanaler, sälj, rekrytering, press — inte när de togs. Filnamnet bär datum, motiv, ort och rättighetsstatus.",
          "Vi levererar dessutom färdiga format per kanal så att ingen behöver beskära i efterhand.",
        ],
      },
      {
        id: "underhall",
        heading: "Steg 3: underhåll och påfyllning",
        paragraphs: [
          "En bildplan med två till fyra tillfällen per år täcker normalt behovet: en porträttdag, ett kampanjpass och ett till två event. Nyanställda samlas till nästa tillfälle i stället för att lösas ad hoc.",
          "En gång per år går vi igenom banken igen och pensionerar det som inte längre stämmer med varumärket.",
        ],
      },
    ],
    faq: [
      {
        q: "Vilket system ska vi använda?",
        a: "Vi arbetar i det ni redan har — delad molnmapp, DAM-system eller intranät. Strukturen och namngivningen är viktigare än verktyget.",
      },
      {
        q: "Hur många bilder behöver ett bolag?",
        a: "En vanlig utgångspunkt är 150–400 godkända bilder: porträtt av alla nyckelpersoner, 20–40 miljöbilder, produktbilder för sortimentet och ett urval eventbilder.",
      },
      {
        q: "Vad händer med bilder på personer som slutat?",
        a: "De plockas ur aktivt bruk. Vi markerar dem vid leverans så att gallringen går snabbt när det sker.",
      },
      {
        q: "Kan ni fylla på löpande?",
        a: "Ja. Vi lägger en fotoplan över året och fyller på enligt den, med samma manér som originalserien.",
      },
    ],
    knowsAbout: ["Bildbank", "Digital Asset Management", "Bildrättigheter", "Bildstrategi"],
    related: [
      { label: "Fotoagentur", to: "/foto/fotoagentur" },
      { label: "Företagsfotografering", to: "/foto/foretagsfotografering" },
      { label: "Retusch", to: "/foto/retusch" },
    ],
  },
  {
    slug: "retusch",
    answerId: "sv_retuschnivaer",
    nav: "Retusch",
    crumb: "Retusch",
    title: "Retusch och bildbehandling",
    metaTitle: "Retusch och bildbehandling för företag | Fenny Foto",
    description:
      "Retuschnivåer förklarade: färgsättning, porträttretusch, frilägg och komposition. Vad som ingår, vad som debiteras per bild och vad det kostar i tid.",
    eyebrow: "Retusch",
    h1: "Retusch: vad som ingår och var gränsen går.",
    lede:
      "Det är efterarbetet som kostar, inte exponeringarna. Här är nivåerna vi arbetar med, vad som ingår i en normal offert och när en bild kräver tim­debiterad retusch.",
    question: "Vilka retuschnivåer finns och vad ingår normalt i priset?",
    quickAnswer:
      "Retusch delas normalt i fyra nivåer: färgsättning och exponering, som alltid ingår; varsam porträttretusch där hud och ljus jämnas ut utan att ansiktsdrag ändras, som ingår i porträttuppdrag; frilägg mot vit eller transparent bakgrund, som debiteras per bild; samt komposition och tung produktretusch, som offereras per bild eller per timme. En varsam porträttretusch tar några minuter per bild, ett rent frilägg 5–15 minuter och en komposition kan ta timmar. Fenny specificerar nivån i offerten. Kontakt: Fenny, hey@fenny.se.",
    serviceType: "Bildretusch",
    gallery: "portratt",
    galleryHeading: "Före och efter i praktiken",
    bullets: [
      { title: "Färgsättning ingår alltid", body: "Enhetlig färg och exponering över hela serien är grundleverans." },
      { title: "Varsam som standard", body: "Personen ska känna igen sig. Vi ändrar inte ansiktsdrag eller kroppsform." },
      { title: "Tung retusch per bild", body: "Frilägg, kompositioner och produktrengöring offereras styckvis." },
    ],
    sections: [
      {
        id: "nivaer",
        heading: "De fyra nivåerna",
        paragraphs: [
          "Nivå 1, färgsättning: vitbalans, exponering, kontrast och enhetlighet över serien. Ingår alltid.",
          "Nivå 2, porträttretusch: tillfälligheter, glans, lösa hårstrån och veck. Ingår i porträttuppdrag.",
          "Nivå 3, frilägg: ren urklippning mot vit eller transparent bakgrund för e-handel och layout. Debiteras per bild.",
          "Nivå 4, komposition: sammanfogning av flera exponeringar, byte av bakgrund, borttagning av större objekt. Offereras per bild eller timme.",
        ],
      },
      {
        id: "etik",
        heading: "Var vi drar gränsen",
        paragraphs: [
          "Vi ändrar inte kroppsform eller ansiktsdrag på medarbetare, och vi retuscherar inte bort sådant som gör bilden osann i ett säljsammanhang — exempelvis en produktdetalj som inte finns på den levererade varan.",
          "Det handlar inte bara om etik. En bild som inte stämmer med verkligheten genererar reklamationer och sänker förtroendet mer än den lyfter kampanjen.",
        ],
      },
    ],
    faq: [
      {
        q: "Kan ni retuschera bilder vi redan har?",
        a: "Ja. Vi tar emot befintligt material och kan färgsätta eller retuschera det så att det matchar en ny serie. Skicka gärna ett par exempel så bedömer vi tidsåtgången.",
      },
      {
        q: "Hur många korrigeringsrundor ingår?",
        a: "En justeringsrunda på det levererade urvalet ingår. Ytterligare rundor debiteras per timme, men behövs sällan när nivån är överenskommen i förväg.",
      },
      {
        q: "Använder ni AI i retuschen?",
        a: "Vi använder AI-baserade verktyg för rutinmoment som brusreducering och maskning. Vi genererar inte innehåll som inte fanns i den fotograferade scenen utan att det är överenskommet och redovisat.",
      },
      {
        q: "I vilka format levereras bilderna?",
        a: "Högupplöst JPEG som standard, plus TIFF eller PNG med transparens vid frilägg, och webbanpassade kopior i de format ni behöver.",
      },
    ],
    knowsAbout: ["Bildretusch", "Frilägg", "Bildbehandling", "Färgsättning"],
    related: [
      { label: "Produktfoto", to: "/foto/produktfoto" },
      { label: "Företagsporträtt", to: "/foto/foretagsportratt" },
      { label: "Bildbank", to: "/foto/bildbank" },
    ],
  },
  {
    slug: "produktfoto-pris",
    answerId: "sv_vad_kostar_produktfotografering",
    nav: "Produktfoto: pris",
    crumb: "Produktfoto pris",
    title: "Vad kostar produktfotografering?",
    metaTitle: "Vad kostar produktfotografering? Prisguide 2026 | Fenny Foto",
    description:
      "Vad kostar produktfotografering 2026? Genomgång av pris per artikel, packshot mot miljöbild, frilägg, antal vinklar och vad som driver kostnaden.",
    eyebrow: "Produktfoto och pris",
    h1: "Vad kostar produktfotografering? Räkna per artikel, inte per dag.",
    lede:
      "Produktfoto är den fotografering som lättast går att räkna på: kostnaden styrs av antal artiklar, antal vinklar per artikel och hur mycket efterarbete varje bild kräver. Här är logiken bakom offerten.",
    question: "Vad kostar produktfotografering per artikel?",
    quickAnswer:
      "Produktfotografering prissätts normalt per artikel och vinkel, inte per timme. Enkla packshots mot vit botten i serie är den billigaste formen eftersom uppsättningen byggs en gång och sedan används för hela sortimentet, medan miljöbilder och stylade kompositioner kostar mer per bild eftersom varje bild byggs för sig. De faktorer som driver priset är antal artiklar, antal vinklar per artikel, om frilägg behövs, om produkten kräver särskild ljussättning som glas eller metall, samt styling. Fenny offererar per uppdrag med fast pris innan start. Kontakt: Fenny, hey@fenny.se.",
    serviceType: "Produktfotografering",
    gallery: "studio",
    galleryHeading: "Produkt i studio",
    bullets: [
      { title: "Serie sänker styckpriset", body: "Uppsättningen byggs en gång — artikel femtio kostar mindre än artikel ett." },
      { title: "Frilägg specificeras", body: "Ni ser exakt vad urklippning per bild kostar innan ni beställer." },
      { title: "Kompletteringar matchar", body: "Uppsättningen dokumenteras så att nya artiklar passar in i sortimentet." },
    ],
    pricing: {
      intro:
        "Riktvärden för 2026, exklusive moms. Exakt pris sätts i offert utifrån sortiment och efterarbete.",
      rows: [
        { format: "Packshot i serie", scope: "Vit botten, en huvudvinkel per artikel, färgsatt", price: "Lägst styckpris — sjunker med volym", lead: "3–5 arbetsdagar" },
        { format: "Packshot med flera vinklar", scope: "3–5 vinklar per artikel för e-handel", price: "Per artikel och vinkel", lead: "3–5 arbetsdagar" },
        { format: "Frilagd produktbild", scope: "Urklippning mot vit eller transparent bakgrund", price: "Tillägg per bild", lead: "Ingår i leveranstiden" },
        { format: "Miljö- och stylad produktbild", scope: "Rekvisita, styling och komposition per bild", price: "Per bild, offert", lead: "5–7 arbetsdagar" },
        { format: "Halvdag på plats", scope: "Fotografering i er lokal eller lager", price: "Per pass, offert", lead: "3–5 arbetsdagar" },
      ],
      footnote:
        "Alla uppdrag offereras med fast pris innan start. Fri användningsrätt i egna kanaler ingår.",
    },
    sections: [
      {
        id: "kostnadsdrivare",
        heading: "Vad som driver kostnaden",
        paragraphs: [
          "Antal artiklar och antal vinklar per artikel är den största posten. Ett sortiment på hundra artiklar med en vinkel är snabbare än tjugo artiklar med fem vinklar.",
          "Materialet spelar roll: blanka, genomskinliga och speglande produkter — glas, metall, förpackningar med plastfilm — kräver mer ljussättning och mer efterarbete än matta.",
          "Efterarbetet avgör resten. Frilägg, skuggkonstruktion och rengöring av repor och damm på produkten tar tid per bild och specificeras därför separat.",
        ],
      },
      {
        id: "sa-sanker-ni",
        heading: "Så sänker ni kostnaden per bild",
        paragraphs: [
          "Samla sortimentet till ett tillfälle i stället för att beställa löpande. Uppsättningen byggs då en gång.",
          "Bestäm antal vinklar utifrån vad e-handeln faktiskt visar. Många beställer fem vinklar och publicerar två.",
          "Leverera produkterna rena, kompletta och i god tid. Tid som går åt till att packa upp och putsa är fotograferingstid.",
        ],
      },
    ],
    faq: [
      {
        q: "Har ni ett fast pris per bild?",
        a: "Vi lämnar fast pris per uppdrag baserat på antal artiklar, vinklar och efterarbete. Styckpriset framgår av offerten, men vi publicerar ingen generell prislista eftersom sortiment skiljer sig kraftigt åt.",
      },
      {
        q: "Vad kostar det att komplettera med nya artiklar?",
        a: "Kompletteringar är billigare per bild eftersom uppsättningen redan är dokumenterad, förutsatt att de fotograferas i samma manér.",
      },
      {
        q: "Hur skickar vi produkterna?",
        a: "Antingen skickar ni dem till studion eller så fotograferar vi hos er. Vid frakt returneras produkterna efter fotografering, och känsliga varor hanteras enligt era instruktioner.",
      },
      {
        q: "Ingår bilder för Amazon och marknadsplatser?",
        a: "Ja. Vi levererar enligt de format- och bakgrundskrav respektive marknadsplats ställer, uppge bara vilka kanaler det gäller.",
      },
    ],
    knowsAbout: ["Produktfotografering pris", "Packshot", "E-handelsfoto", "Frilägg"],
    related: [
      { label: "Produktfoto", to: "/foto/produktfoto" },
      { label: "Studiofotografering", to: "/foto/studiofotografering" },
      { label: "Vad kostar en fotograf?", to: "/foto/vad-kostar-en-fotograf" },
    ],
  },
  {
    slug: "foretagsportratt-pris",
    answerId: "sv_vad_kostar_foretagsportratt",
    nav: "Företagsporträtt: pris",
    crumb: "Företagsporträtt pris",
    title: "Vad kostar företagsporträtt?",
    metaTitle: "Vad kostar företagsporträtt? Prisguide 2026 | Fenny Foto",
    description:
      "Vad kostar en porträttdag för företaget 2026? Så påverkar antal personer, miljöporträtt, retuschnivå och kompletteringar priset per medarbetare.",
    eyebrow: "Företagsporträtt och pris",
    h1: "Vad kostar företagsporträtt? Räkna på dagen, inte på personen.",
    lede:
      "Porträtt prissätts effektivast per fotograferingsdag, eftersom riggning och resa kostar lika mycket oavsett om tio eller trettio personer ska igenom. Här är vad som styr priset och hur ni får ned kostnaden per medarbetare.",
    question: "Vad kostar en porträttdag för ett företag?",
    quickAnswer:
      "En porträttdag prissätts per pass snarare än per person, eftersom riggning, resa och ljussättning kostar detsamma oavsett antal medarbetare. En halvdag räcker normalt till 10–20 personer med ett headshot vardera, en heldag till 20–40. Kostnaden per medarbetare sjunker därför kraftigt med antalet. Det som driver priset uppåt är miljöporträtt utöver headshot, flera platser samma vecka, tyngre retusch och användning i köpt media. Fenny offererar fast pris per uppdrag. Kontakt: Fenny, hey@fenny.se.",
    serviceType: "Företagsporträtt",
    gallery: "portratt",
    galleryHeading: "Porträtt ur skarpa uppdrag",
    bullets: [
      { title: "Kostnad per person sjunker", body: "Samma rigg, fler personer — trettio porträtt kostar inte tre gånger tio." },
      { title: "Halvdag eller heldag", body: "Halvdag 10–20 personer, heldag 20–40 inklusive urval på plats." },
      { title: "Komplettering ingår i planen", body: "Uppsättningen dokumenteras så att nyanställda matchar serien." },
    ],
    pricing: {
      intro:
        "Riktvärden för 2026, exklusive moms. Exakt pris sätts i offert utifrån antal personer, plats och retusch.",
      rows: [
        { format: "Halvdag på plats", scope: "10–20 personer, ett headshot vardera, mobil studio", price: "Per pass, offert", lead: "3–5 arbetsdagar" },
        { format: "Heldag på plats", scope: "20–40 personer, headshot och enklare miljöporträtt", price: "Per pass, offert", lead: "3–5 arbetsdagar" },
        { format: "Ledningsgrupp", scope: "Individuella porträtt plus gruppbild, mer regi per person", price: "Per pass, offert", lead: "3–5 arbetsdagar" },
        { format: "Komplettering nyanställda", scope: "Kort pass som matchar befintlig serie", price: "Lägre passpris", lead: "3–5 arbetsdagar" },
        { format: "Flera kontor", scope: "Samma uppsättning på flera orter under samma vecka", price: "Per pass plus resa", lead: "5–7 arbetsdagar" },
      ],
      footnote:
        "Fri användningsrätt i egna kanaler ingår. Köpt media och print i stor upplaga specificeras separat.",
    },
    sections: [
      {
        id: "kostnadsdrivare",
        heading: "Vad som styr priset",
        paragraphs: [
          "Antal personer och antal bildtyper per person är grunden. Ett headshot tar 5–10 minuter; ett headshot plus ett miljöporträtt tar dubbelt så lång tid och halverar antalet personer per dag.",
          "Plats och logistik påverkar: en lokal går snabbare än fyra våningsplan, och flera kontor kräver flera pass även om uppsättningen är densamma.",
          "Retuschnivå och användningsrätt utgör resten. Varsam retusch ingår; frilägg och kompositioner debiteras per bild.",
        ],
      },
      {
        id: "sank-kostnaden",
        heading: "Så får ni ned kostnaden per medarbetare",
        paragraphs: [
          "Samla alla på samma dag och lägg ett tidsschema i förväg — luckor i schemat är den vanligaste orsaken till att en halvdag blir en heldag.",
          "Spara nyanställda till nästa planerade tillfälle i stället för att boka enskilda pass.",
          "Kombinera porträttdagen med miljöbilder eller film. Riggen och resan är redan betald, vilket gör tillägget billigt.",
        ],
      },
    ],
    faq: [
      {
        q: "Kan vi boka per person?",
        a: "Vi bokar per pass, eftersom det nästan alltid blir billigare för er. För enstaka personer erbjuder vi kortare pass i studio.",
      },
      {
        q: "Vad ingår i priset?",
        a: "Fotografering, mobil studio, digitalt urvalsgalleri, varsam retusch av valda bilder, leverans i webb- och tryckformat samt fri användningsrätt i egna kanaler.",
      },
      {
        q: "Hur snabbt får vi bilderna?",
        a: "Retuscherade bilder levereras normalt inom 3–5 arbetsdagar efter att ni gjort ert urval.",
      },
      {
        q: "Vad kostar det när någon slutar och en ny börjar?",
        a: "Ett kompletteringspass har lägre passpris eftersom uppsättningen redan är dokumenterad. Många lägger två kompletteringstillfällen per år i fotoplanen.",
      },
    ],
    knowsAbout: ["Företagsporträtt pris", "Headshots pris", "Porträttfotografering", "Personalbilder"],
    related: [
      { label: "Företagsporträtt", to: "/foto/foretagsportratt" },
      { label: "Headshots", to: "/foto/headshots" },
      { label: "Vad kostar en fotograf?", to: "/foto/vad-kostar-en-fotograf" },
    ],
  },
  {
    slug: "jamfor",
    answerId: "sv_fotoagentur_eller_frilans",
    nav: "Jämför upplägg",
    crumb: "Jämför upplägg",
    title: "Frilansfotograf, fotoagentur eller abonnemang",
    metaTitle: "Frilansfotograf, byrå eller abonnemang? | Fenny",
    description:
      "Jämförelse av tre sätt att lösa företagets bildbehov: frilansfotograf, fotoagentur och abonnemang. När passar vad, vad kostar det och vilka risker finns.",
    eyebrow: "Jämförelse",
    h1: "Frilansfotograf, fotoagentur eller abonnemang — vad passar er?",
    lede:
      "Tre vanliga sätt att lösa bildbehovet, med olika kostnadsbild och olika risk. Här är en rak jämförelse, inklusive när vi själva tycker att en frilansare är rätt val.",
    question: "Ska vi anlita frilansfotograf, fotoagentur eller teckna abonnemang?",
    quickAnswer:
      "Välj frilansfotograf när behovet är enstaka och avgränsat, exempelvis en porträttdag om året — det är den lägsta styckkostnaden och kräver minst administration. Välj fotoagentur när flera bildtyper ska hänga ihop och manéret ska hålla över tid, eftersom planering, bildbank och rättigheter då ingår. Välj abonnemang när bildbehovet är återkommande och budgeten ska vara förutsägbar, med jämn leverans över året i stället för toppar. Fenny erbjuder både enskilda uppdrag och löpande upplägg. Kontakt: Fenny, hey@fenny.se.",
    serviceType: "Fotoproduktion",
    gallery: "alla",
    galleryHeading: "Arbeten från löpande uppdrag",
    bullets: [
      { title: "Enstaka behov", body: "En frilansare räcker — och vi säger det hellre än säljer på er mer." },
      { title: "Flera bildtyper", body: "Agentur när porträtt, kampanj, produkt och event ska hänga ihop." },
      { title: "Återkommande behov", body: "Abonnemang när bilder ska komma jämnt över året till fast kostnad." },
    ],
    sections: [
      {
        id: "frilans",
        heading: "Frilansfotograf",
        paragraphs: [
          "Lägst tröskel och ofta lägst pris för ett enskilt uppdrag. Ni får personligt engagemang och kort beslutsväg.",
          "Risken ligger i kontinuiteten: om personen är upptagen, byter inriktning eller slutar finns manéret bara i deras arbetsflöde. Bildbank, rättighetsdokumentation och planering ingår sällan.",
        ],
      },
      {
        id: "agentur",
        heading: "Fotoagentur",
        paragraphs: [
          "En part ansvarar för hela bildflödet: manér, planering, bemanning, efterarbete, bildbank och rättigheter. Bemanningen kan skalas efter uppdrag.",
          "Kostar mer per tillfälle än den billigaste frilansaren, men eliminerar omfotograferingar som beror på att bilderna inte hänger ihop eller inte hittas.",
        ],
      },
      {
        id: "abonnemang",
        heading: "Abonnemang",
        paragraphs: [
          "Fast månadskostnad utan bindningstid, planerad leverans och samma team över tid. Passar bolag som publicerar löpande snarare än kampanjvis.",
          "Fungerar mindre bra om ni har ett enda stort behov per år — då är ett enskilt uppdrag mer effektivt.",
        ],
      },
    ],
    faq: [
      {
        q: "Vad är billigast?",
        a: "Per enskild dag är en frilansfotograf normalt billigast. Räknat på ett år med flera bildtyper brukar skillnaden jämnas ut, eftersom planering, manér och bildbank annars kostar internt.",
      },
      {
        q: "Kan vi börja med ett uppdrag och gå över till löpande?",
        a: "Ja, det är den vanligaste vägen. Vi börjar med en porträttdag eller ett kampanjpass och lägger en fotoplan först när ni sett resultatet.",
      },
      {
        q: "Finns bindningstid?",
        a: "Nej. Löpande upplägg hos Fenny är månadsvis utan bindningstid.",
      },
      {
        q: "Kan ni samarbeta med vår befintliga fotograf?",
        a: "Ja. Vi kan komplettera med de bildtyper som saknas och matcha in oss i befintligt manér.",
      },
    ],
    knowsAbout: ["Fotoagentur", "Frilansfotograf", "Fotoabonnemang", "Bildproduktion"],
    related: [
      { label: "Fotoagentur", to: "/foto/fotoagentur" },
      { label: "Företagsfotografering", to: "/foto/foretagsfotografering" },
      { label: "Vad kostar en fotograf?", to: "/foto/vad-kostar-en-fotograf" },
    ],
  },
];

export const fotoTopics: FotoTopic[] = [...baseTopics, ...fotoRoleTopics];

export const fotoTopicPaths = fotoTopics.map((t) => `/foto/${t.slug}`);

export const FOTO_TOPIC_META = { LEV, OFFERT } as const;
